import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, parseISO, startOfMonth, endOfMonth, isValid } from 'date-fns';
import { hu } from 'date-fns/locale';
import { formatServerDateTime, formatDateOnlyRange } from '../../utils/dateUtils';
import coachPaymentService from '../../services/coachPaymentService';
import { userService } from '../../services/userService';
import { trainingBalanceService } from '../../services/trainingBalanceService';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty } from '../../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import type { Theme } from '@mui/material/styles';
import { paymentSummaryColors, PAYMENT_SUMMARY_ELEVATION } from '../../theme/readableColors';
import { onSessionRestored } from '../../utils/session';
import { UserListItem } from '../../types/user';
import {
  CoachPayment,
  PayableEvents,
  PayableEvent,
  PaymentStatusDisplayNames,
  PaymentStatusColors,
  CustomPaymentItem,
  PaymentItemCategory,
  PaymentItemCategoryDisplayNames
} from '../../types/coachPayment';
import { isForintInputAccepted, parseForintInput, forintFieldValue, MAX_CUSTOM_ITEM_AMOUNT } from '../../utils/money';

/** Teljes, valós naptári dátum (`yyyy-MM-dd`), ésszerű évvel — a gépelés közbeni félkész értékek kiszűrésére. */
const isPlausibleDate =(value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  if (year < 2000 || year > 2100) return false;
  const parsed = parseISO(value);
  return isValid(parsed) && format(parsed, 'yyyy-MM-dd') === value;
};

interface Coach {
  userId: string;
  name: string;
  hourlyRate?: number;
}

/**
 * [Web 6 W2, 2026-10-05] KIJELÖLHETŐ (kifizethető) edzés = még KIFIZETETLEN és az edző JELEN volt.
 *
 * MI VOLT A BAJ: az „Összes kijelölése” csak a `!isPaid` feltételt nézte, így a „Hiányzott” sorokat
 * is kijelölte, a szerver pedig az EGÉSZ kifizetést 400-zal visszadobta („Csak olyan esemény
 * fizethető ki, amelyen az edző jelen volt.” — `backend/PinyoApi/Controllers/CoachPaymentController.cs:323-335`).
 * A fejléc „Kifizetendő” száma (`CoachPaymentController.cs:191-198`: `!IsPaid && CoachAttended`)
 * már eddig is csak a jelen-sorokat számolta — a kijelölés most UGYANEZT a fogalmat követi.
 */
const isSelectableEvent = (e: Pick<PayableEvent, 'isPaid' | 'coachAttended'>): boolean =>
  !e.isPaid && e.coachAttended;

/** A tiltott (Hiányzott) jelölő tooltipje — a teszt is ezt a szöveget keresi. */
const ABSENT_NOT_PAYABLE_TOOLTIP = 'Az edző nem volt jelen, ez az edzés nem fizethető ki';

const CoachPaymentManagement: React.FC = () => {
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();
  const [activeTab, setActiveTab] = useState(0);

  // Coach selection
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [loadingCoaches, setLoadingCoaches] = useState(false);

  // Date range
  const [startDate, setStartDate] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  // Payable events
  const [payableEventsData, setPayableEventsData] = useState<PayableEvents | null>(null);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  /**
   * [Web 6 W2, 2026-10-05] „Csak ahol az edző jelen volt” szűrő — ALAPBÓL BE (klubvezetés: „ebből
   * van a legtöbb”). Csak a MEGJELENÍTÉST szűri; a kijelölhetőséget az `isSelectableEvent` dönti el.
   */
  const [attendedOnly, setAttendedOnly] = useState(true);

  // Custom payment items
  const [customItems, setCustomItems] = useState<CustomPaymentItem[]>([]);
  /**
   * Az összeg kezdőértéke `undefined` (ÜRES mező = „nincs megadva"), NEM 0 [NB-8, 2026-09-14].
   * A 0 Ft ÉRDEMI ÉRTÉK („ingyen") — a két állapotot nem szabad összemosni, lásd `utils/money.ts`.
   */
  const [customItemForm, setCustomItemForm] = useState<Partial<CustomPaymentItem>>({
    category: 'Travel' as PaymentItemCategory,
    description: '',
    date: new Date(),
    hours: undefined,
    amount: undefined
  });

  // Payments list
  const [payments, setPayments] = useState<CoachPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  /** [5. javító kör, F3] az előzmény betöltési hibája — a „Kifizetések Listája” fülön látszik. */
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  // Create payment
  const [adminNotes, setAdminNotes] = useState('');
  const [creating, setCreating] = useState(false);
  /**
   * [5. javító kör, kiegészítés] Dupla kattintás elleni őr: a `creating` állapot csak a következő
   * újrarajzolás után tiltja a gombot, a ref AZONNAL. (Csak egyéb tételből álló kifizetésnél a
   * szerver sem szűr duplát — két egyforma kifizetés jönne létre.)
   */
  const creatingRef = useRef(false);
  /** [5. javító kör, F4] a létrehozás hibája a NYITVA maradó dialógusban is látszik. */
  const [createError, setCreateError] = useState<string | null>(null);

  // Dialogs
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Coach financial summary
  const [totalCollected, setTotalCollected] = useState<number | null>(null);
  const [calculatedCompensation, setCalculatedCompensation] = useState<number | null>(null);
  const [loadingCollected, setLoadingCollected] = useState(false);

  // Errors
  const [error, setError] = useState<string | null>(null);

  /**
   * [5. javító kör, F1, 2026-10-03] A képernyőn MINDENKOR választott (edző, időszak) pár.
   *
   * MI VOLT A BAJ: a létrehozás siker-ága (`await loadPayableEvents()`) a KATTINTÁSKORI closure-ből
   * futott — ha közben edzőt váltottak (a dialógus Esc-kel zárható volt), a RÉGI edző listáját
   * kérte le, és mint legutolsó sorszám eldobta az új edző helyes válaszát: B neve alatt A listája
   * és A előzménye látszott (a validátor élő Chrome-ban mérte, `v4val/probes4.spec.ts` P10).
   * MOST: a betöltők ebből a ref-ből olvasnak (nem a closure-ből), és a dialógus a létrehozás
   * alatt nem zárható.
   *
   * A VÉDELEM RÉTEGEI [6. javító kör pontosítása, 5. validátor lelete]: az ELSŐDLEGES védelem a
   * `handleCreatePayment` siker-ágának `sameCoach`/`samePeriod` kapuja — az ürítés és az újratöltés
   * csak akkor fut, ha a választás a kérés óta nem változott (ehhez olvassa ezt a ref-et, nem a
   * kattintáskori closure-t). Az, hogy a betöltők is a ref-ből olvasnak, a MÁSODIK védvonal: ha a
   * kapu mégis átengedne egy újratöltést, az is a mindenkori választásra futna.
   */
  const selectionRef = useRef({ coachId: selectedCoachId, start: startDate, end: endDate });
  selectionRef.current = { coachId: selectedCoachId, start: startDate, end: endDate };

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] A kifizetés ÖSSZEÁLLÍTÁSA mentetlen
   * adat: a kijelölt edzések (`selectedEventIds`), a felvett egyéni tételek (`customItems`), a
   * félig kitöltött tétel-űrlap és az admin megjegyzés. Lelet: `CoachPaymentManagement.tsx:79/83/101`
   * (korábbi sorok) — a Dashboard fülváltása kérdés nélkül lebontotta a képernyőt. Az alapvonal
   * az ÜRES összeállítás (a betöltött edző/időszak NEM beírt adat).
   */
  const isDirty = isFormDirty(
    {
      selectedEventIds: [] as string[],
      customItems: [] as CustomPaymentItem[],
      adminNotes: '',
      draftDescription: '',
      draftAmount: undefined as number | undefined,
      draftHours: undefined as number | undefined,
    },
    {
      selectedEventIds,
      customItems,
      adminNotes,
      draftDescription: customItemForm.description,
      draftAmount: customItemForm.amount,
      draftHours: customItemForm.hours,
    },
  );
  useUnsavedChanges(isDirty, 'Kifizetés összeállítása');

  /**
   * IDŐSZAK-váltás (kezdő/záró dátum) és „Frissítés”: újratölti a kifizethető edzéseket, és a
   * `loadPayableEvents` KIÜRÍTI a kijelölést. Ha van kijelölés, előbb kérdezünk. Az egyéni tételek
   * és a megjegyzés ezt a váltást túlélik (ugyanannak az edzőnek a kifizetése marad) — a kérdés
   * szövege ezt ki is mondja, hogy ne ígérjen a valóságosnál több veszteséget.
   */
  const confirmDropSelection = async (action: 'period' | 'refresh'): Promise<boolean> => {
    if (selectedEventIds.length === 0) return true;
    const what = action === 'period' ? 'Ha most időszakot váltasz' : 'Ha most frissítesz';
    return confirm({
      title: 'Mentetlen változás',
      message:
        `${selectedEventIds.length} kijelölt edzés van a kifizetésben. ${what}, a kijelölés elvész ` +
        '(az egyéb tételek és a megjegyzés megmaradnak).',
      confirmLabel: action === 'period' ? 'Váltás' : 'Frissítés',
      cancelLabel: 'Mégse',
      destructive: true,
    });
  };

  /**
   * [Web UX 2. csomag – 3. javító kör, validátor F-2 (pénzügyi), 2026-10-03] EDZŐ-VÁLTÁS.
   *
   * MI VOLT A BAJ: edző-váltáskor csak a kijelölt edzések ürültek (a `loadPayableEvents` által), az
   * egyéni tételek (`customItems`), a félig kitöltött tétel és az admin megjegyzés (`adminNotes`)
   * ÁTKERÜLTEK az új edzőre — a `handleCreatePayment` a PILLANATNYI edzőhöz küldi őket, tehát pl.
   * az A edző útiköltsége a B edző kifizetésébe került volna. A kérdés ráadásul csak kijelölt
   * edzésnél jött, egyéni tételnél nem.
   *
   * DÖNTÉS (koordinátori ideiglenes alapértelmezés): edző-váltáskor a TELJES összeállítás ürül, és
   * a kérdés akkor jön, ha BÁRMELYIK rész nem üres; a szöveg felsorolja, mi vész el.
   * Őr: `__tests__/CoachPaymentManagement.unsaved.test.tsx` („edző-váltás – egyéni tétel”).
   */
  const confirmCoachChange = async (): Promise<boolean> => {
    const lost: string[] = [];
    if (selectedEventIds.length > 0) lost.push(`${selectedEventIds.length} kijelölt edzés`);
    if (customItems.length > 0) lost.push(`${customItems.length} egyéb tétel`);
    if (
      (customItemForm.description ?? '').trim() !== '' ||
      customItemForm.amount !== undefined ||
      customItemForm.hours !== undefined
    ) {
      lost.push('a félig kitöltött tétel');
    }
    if (adminNotes.trim() !== '') lost.push('a megjegyzés');
    if (lost.length === 0) return true;
    return confirm({
      title: 'Mentetlen változás',
      message:
        `Ha másik edzőre váltasz, a kifizetés összeállítása elvész: ${lost.join(', ')}. ` +
        'Egy másik edző kifizetésébe nem kerülhet át tétel.',
      confirmLabel: 'Váltás',
      cancelLabel: 'Mégse',
      destructive: true,
    });
  };

  const resetComposition = () => {
    setSelectedEventIds([]);
    setCustomItems([]);
    setCustomItemForm({
      category: 'Travel' as PaymentItemCategory,
      description: '',
      date: new Date(),
      hours: undefined,
      amount: undefined,
    });
    setAdminNotes('');
  };

  /**
   * [3. javító kör, validátor M, 2026-10-03] A dátum-mezők a GÉPELÉS közben nem kérdeznek.
   *
   * MI VOLT A BAJ: a `type="date"` mező minden `onChange`-re kérdezett — kézi gépelésnél (a Chrome
   * az évet jegyenként `0002-…`, `0020-…`, `0202-…` alakban adja) már az első leütésre feljött a
   * kérdés, és egy félkész dátummal újra is töltött volna.
   *
   * MOST: a mező SAJÁT bemeneti állapotot tart (`…DateInput`); csak ÉRVÉNYES, ésszerű évű
   * (2000–2100), a mostanitól ELTÉRŐ teljes dátum kerül a kérdés elé és — elfogadás után — az
   * érvényes időszakba (ami az újratöltést indítja). „Mégse”-re és a félkész érték elhagyásakor
   * (blur) a mező visszaáll az érvényes időszakra. ⚠️ A 4. kör óta ez NEM gépelés közben, hanem a
   * bevitel befejezésekor (blur/Enter) történik — lásd lent, `commitDateInput`.
   */
  const [startDateInput, setStartDateInput] = useState<string>(startDate);
  const [endDateInput, setEndDateInput] = useState<string>(endDate);

  /**
   * [4. javító kör, validátor F-A (pénzügyi), 2026-10-03] Az időszak CSAK a bevitel BEFEJEZÉSEKOR vált.
   *
   * MI VOLT A BAJ: a 3. kör az `onChange`-en váltott, ha az érték „érvényes, ésszerű évű" dátum volt.
   * A Chrome `type="date"` mezője viszont a NAP- és HÓNAP-szakaszban már az ELSŐ számjegyre teljes,
   * ÉRVÉNYES dátumot ad (a „25" gépelésekor előbb `…-02`, majd `…-25`) — így kijelölés mellett az
   * első leütésre feljött a kérdés, a második leütés a dialógusba ment, és „Váltás" után a KÖZTES
   * dátum (`…-02`) lett az időszak; kijelölés nélkül pedig minden leütés külön kérést indított.
   * Gépelés közben ezért NEM lehet eldönteni, hogy az érték „kész"-e.
   *
   * MOST: az `onChange` CSAK a helyi bemeneti állapotot frissíti. A váltás (kérdés + újratöltés)
   * a mező ELHAGYÁSAKOR (`blur`) vagy ENTER-re történik (`commitDateInput`). Érvénytelen/félkész
   * értéknél a mező visszaáll az érvényes időszakra.
   *
   * DÖNTÉS a böngésző naptár-választójáról: a választóból való kiválasztás a Chrome-ban billentyű
   * nélküli `input`/`change` esemény, de ugyanilyen eseményt ad a felolvasó/hang-bevitel és a
   * Playwright `fill` is — egy „nem volt keydown ⇒ naptár" heurisztika tehát NEM megbízható, és a
   * hibás besorolás épp a fenti köztes-dátum hibát hozná vissza. Ezért a naptárból választott
   * dátum is a mező elhagyásakor/Enterre érvényesül; addig a mező alatt súgó jelzi, hogy a dátum
   * még nincs betöltve, és a „Kifizetés létrehozása" TILTOTT (`canCreatePayment`), tehát a régi
   * időszak listája nem mehet ki az új időszakkal.
   * Őr: `__tests__/CoachPaymentManagement.unsaved.test.tsx` („Chrome-szerű gépelési sorozat”) és
   * `tests/e2e/flow-payment-period-input.spec.ts` (valódi `keyboard.type`).
   */
  const committingDateRef = useRef(false);

  const handleDateInput = (which: 'start' | 'end', next: string) => {
    (which === 'start' ? setStartDateInput : setEndDateInput)(next);
  };

  const commitDateInput = async (which: 'start' | 'end') => {
    // A kérdés-ablak megnyitása elveszi a fókuszt (újabb `blur`) — egyszerre csak EGY érvényesítés fusson.
    if (committingDateRef.current) return;
    const input = which === 'start' ? startDateInput : endDateInput;
    const setInput = which === 'start' ? setStartDateInput : setEndDateInput;
    const setCommitted = which === 'start' ? setStartDate : setEndDate;
    const committed = which === 'start' ? startDate : endDate;
    if (input === committed) return;
    if (!isPlausibleDate(input)) {
      setInput(committed);
      return;
    }
    committingDateRef.current = true;
    try {
      if (await confirmDropSelection('period')) {
        invalidatePayableEvents();
        setCommitted(input);
      } else {
        setInput(committed);
      }
    } finally {
      committingDateRef.current = false;
    }
  };

  const handleDateKeyDown = (which: 'start' | 'end', e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commitDateInput(which);
    }
  };

  /** A mezőben olyan (érvényes) dátum áll, ami még NINCS betöltve — a súgó és a létrehozás-tiltás alapja. */
  const datePending = (which: 'start' | 'end') => {
    const input = which === 'start' ? startDateInput : endDateInput;
    return isPlausibleDate(input) && input !== (which === 'start' ? startDate : endDate);
  };

  // Load coaches on mount
  useEffect(() => {
    loadCoaches();
  }, []);

  // Load payable events when coach or dates change
  useEffect(() => {
    if (selectedCoachId && startDate && endDate) {
      loadPayableEvents();
      loadCoachPayments();
      loadCoachCollected();
    }
  }, [selectedCoachId, startDate, endDate]);

  const loadCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const data = await userService.getCoaches();
      setCoaches(data.map((c: UserListItem) => ({
        userId: c.userId,
        name: c.name,
        hourlyRate: c.hourlyRate
      })));
    } catch (err) {
      console.error('Failed to load coaches:', err);
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Nem sikerült betölteni az edzőket'));
    } finally {
      setLoadingCoaches(false);
    }
  };

  /**
   * [4. javító kör, validátor F-B (pénzügyi), 2026-10-03] KÉRÉS-SORSZÁM a kifizethető edzésekre.
   *
   * MI VOLT A BAJ: a `loadPayableEvents` minden válasza beírt a listába, sorrendtől függetlenül. Egy
   * KÉSVE érkező RÉGI válasz (korábbi időszak vagy korábbi edző) felülírta a frissebbet: a mező
   * `2026-10-25`-öt mutatott, a lista a `…-02`-i eseményeket, és a létrehozás `periodStart:
   * 2026-10-25` + a `02`-i `eventIds`-szel ment ki. A szerver ezt NEM szűri ki
   * (`backend/PinyoApi/Controllers/CoachPaymentController.cs:309-317` nem veti össze az időszakot az
   * eseményekkel), tehát a kliensnek kell garantálnia.
   *
   * MOST: minden kérés sorszámot kap; csak a LEGUTÓBBI kérés válasza írhat a listába, a hibába és a
   * töltés-jelzőbe. Edző- és időszak-váltáskor a régi lista AZONNAL ürül (`invalidatePayableEvents`),
   * és a `loadedPeriod` rögzíti, melyik (edző, időszak) párhoz tartozik a látott lista — a létrehozás
   * EZT küldi, és tiltott, ha a képernyőn választott pár ettől eltér (`canCreatePayment`).
   */
  const payableSeqRef = useRef(0);
  // Ugyanaz a sorszám-elv a kifizetés-előzményre és a „Beszedett/Fizetendő” összesítőre: edző-váltás
  // után a régi edző késői válasza ne írja felül az új edző adatait.
  const paymentsSeqRef = useRef(0);
  const collectedSeqRef = useRef(0);
  const [loadedPeriod, setLoadedPeriod] = useState<{ coachId: string; start: string; end: string } | null>(null);

  const invalidatePayableEvents = () => {
    payableSeqRef.current += 1;
    setPayableEventsData(null);
    setLoadedPeriod(null);
    setSelectedEventIds([]);
    // [7. javító kör, M1] a régi (edző, időszak) betöltési hibája sem marad az új mellett
    setPayableError(null);
    payableFailedBySessionRef.current = false;
    // [5. javító kör, F2] a „Beszedett / Keresett / Fizetendő” összesítő is (edzőtől ÉS időszaktól
    // függ): a régi számok ne álljanak az új lista mellett, és a régi késői válasz se írhasson be.
    collectedSeqRef.current += 1;
    setTotalCollected(null);
    setCalculatedCompensation(null);
    setLoadingCollected(false);
    // [8. javító kör, F-1] a régi (edző, időszak) összesítő-hibája sem töltődik újra az új helyett
    collectedFailedRef.current = false;
    collectedFailedBySessionRef.current = false;
  };

  /**
   * [5. javító kör, F3] Edző-váltáskor a kifizetés-ELŐZMÉNY is azonnal ürül (a régi edző kifizetései
   * a Kifizetve/Törlés gombokkal együtt nem maradhatnak az új edző neve alatt), és a régi késői
   * válasza érvénytelen.
   */
  const invalidateCoachPayments = () => {
    paymentsSeqRef.current += 1;
    setPayments([]);
    setPaymentsError(null);
    paymentsFailedBySessionRef.current = false; // [8. javító kör, F-1]
  };

  /**
   * [7. javító kör — validátor M1, 2026-10-04] A kifizethető-lista BETÖLTÉSI hibája külön állapot, a
   * lista helyén látszik egy „Újrapróbálás" gombbal.
   *
   * MI VOLT A BAJ: hibánál a lap tetején a fix „Nem sikerült betölteni a kifizetendő edzéseket" jött
   * (lejáratkor is — nem `requestErrorMessage`), a lista helyén pedig a „Válassz edzőt és időszakot…"
   * súgó, holott mindkettő ki volt választva; az egyéb tételek és a létrehozás gomb eltűnt, és
   * újra-belépés után sem töltődött újra semmi.
   * MOST: `requestErrorMessage` + a lista helyén a hiba és az „Újrapróbálás"; ha a betöltés a
   * LEJÁRAT miatt bukott (`payableFailedBySessionRef`), a sikeres újra-belépés után magától újratölt
   * (`onSessionRestored`).
   */
  const [payableError, setPayableError] = useState<string | null>(null);
  const payableFailedBySessionRef = useRef(false);

  /**
   * @param keepSelection [7. javító kör — validátor M2] `true`: a kijelölés NEM ürül; a betöltés után
   *   azokra az edzésekre marad meg, amelyek az új listában is kifizethetők (metszet). KORÁBBAN egy
   *   RÉGI kifizetés törlése (`handleDeletePayment`) kérdés nélkül ürítette a folyamatban lévő
   *   összeállítás kijelölését. Az időszak-/edző-váltás és a „Frissítés" ürítése VÁLTOZATLAN (ott a
   *   felhasználó előbb megerősíti, `confirmDropSelection`/`confirmCoachChange`).
   */
  const loadPayableEvents = async (options?: { keepSelection?: boolean }) => {
    const keepSelection = options?.keepSelection === true;
    // [5. javító kör, F1] a MINDENKORI választás (ref), nem a hívó closure-je
    const request = { ...selectionRef.current };
    if (!request.coachId) return;
    const seq = ++payableSeqRef.current;
    const isCurrent = () => seq === payableSeqRef.current;

    try {
      setLoadingEvents(true);
      setError(null);
      setPayableError(null);
      payableFailedBySessionRef.current = false;
      setPayableEventsData(null);
      setLoadedPeriod(null);
      if (!keepSelection) setSelectedEventIds([]);
      // ⚠️ A `new Date('yyyy-MM-dd')` itt SZÁNDÉKOS (UTC-éjfél): a szolgáltatás `toISOString()`-gel
      // küldi, így a szerver pontosan a választott napot kapja (`…T00:00:00.000Z`). A helyi éjfél
      // (`parseDateLocal`) `toISOString()`-je az ELŐZŐ napot adná — ehhez a sorhoz ne nyúlj.
      const data = await coachPaymentService.getPayableEvents(
        request.coachId,
        new Date(request.start),
        new Date(request.end)
      );
      if (!isCurrent()) return;
      setPayableEventsData(data);
      setLoadedPeriod(request);
      if (keepSelection) {
        // [7. javító kör, M2] metszet: csak az új listában is szereplő, még KIFIZETETLEN edzés marad
        // [Web 6 W2, 2026-10-05] a metszet is a KIJELÖLHETŐ fogalmat követi (kifizetetlen ÉS jelen):
        // ha közben a jelenléti íven az edző „Hiányzott”-ra vált, a sor kikerül a kijelölésből.
        const stillPayable = new Set(data.events.filter(isSelectableEvent).map((e) => e.eventId));
        setSelectedEventIds((prev) => prev.filter((id) => stillPayable.has(id)));
      } else {
        setSelectedEventIds([]);
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Failed to load payable events:', err);
      // [7. javító kör, M1] lejáratkor a rövid lejárat-mondat, egyébként a szerver indoklása
      setPayableError(requestErrorMessage(err, 'Nem sikerült betölteni a kifizetendő edzéseket'));
      payableFailedBySessionRef.current = (err as any)?.response?.status === 401;
    } finally {
      if (isCurrent()) setLoadingEvents(false);
    }
  };

  /**
   * [8. javító kör — 6. validátor F-1, 2026-10-04] Mindhárom betöltés (kifizethető lista,
   * kifizetés-előzmény, „Beszedett / Fizetendő” összesítő) saját „lejárat miatt bukott” jelzőt kap.
   * KORÁBBAN az újra-belépés csak a kifizethető listát töltötte újra: a „Kifizetések Listája” fülön
   * a „Lejárt a bejelentkezésed…” hiba ott maradt, az összesítő pedig `null` maradt (eltűnt).
   * MOST: a sikeres újra-belépés után MINDEN lejárat miatt bukott betöltés újrafut a MINDENKORI
   * választásra (a betöltők a `selectionRef`-ből olvasnak, és a sorszám-elv védi őket).
   */
  const paymentsFailedBySessionRef = useRef(false);
  const collectedFailedBySessionRef = useRef(false);
  // [8. javító kör, F-1] az összesítő betöltési hibája (bármi okból) — az „Újrapróbálás” ezt is újratölti
  const collectedFailedRef = useRef(false);

  // [7. javító kör, M1] sikeres újra-belépés után a LEJÁRAT miatt bukott lista magától újratöltődik
  // (a kijelölést megtartva — a lejárat nem a felhasználó döntése volt). A ref-ből hívunk, hogy a
  // feliratkozás ne a legelső render `loadPayableEvents`-ét lássa.
  // [8. javító kör, F-1] ugyanígy a kifizetés-előzmény és az összesítő is.
  const reloadAfterRestoreRef = useRef<() => void>(() => {});
  reloadAfterRestoreRef.current = () => {
    if (payableFailedBySessionRef.current) {
      payableFailedBySessionRef.current = false;
      void loadPayableEvents({ keepSelection: true });
    }
    if (paymentsFailedBySessionRef.current) {
      paymentsFailedBySessionRef.current = false;
      void loadCoachPayments();
    }
    if (collectedFailedBySessionRef.current) {
      collectedFailedBySessionRef.current = false;
      void loadCoachCollected();
    }
  };
  useEffect(() => onSessionRestored(() => reloadAfterRestoreRef.current()), []);

  /**
   * [8. javító kör, F-1] Az „Újrapróbálás” a kifizethető listán kívül a többi HIBÁS betöltést is
   * újratölti (előzmény, összesítő) — a sikereseket nem kéri le feleslegesen.
   */
  const retryFailedLoads = () => {
    void loadPayableEvents({ keepSelection: true });
    if (paymentsError !== null) void loadCoachPayments();
    if (collectedFailedRef.current) void loadCoachCollected();
  };

  const loadCoachPayments = async () => {
    const coachId = selectionRef.current.coachId; // [5. javító kör, F1] ref, nem closure
    if (!coachId) return;
    const seq = ++paymentsSeqRef.current;

    try {
      setLoadingPayments(true);
      setPaymentsError(null);
      paymentsFailedBySessionRef.current = false;
      const data = await coachPaymentService.getCoachPayments(coachId);
      if (seq !== paymentsSeqRef.current) return;
      setPayments(data);
    } catch (err) {
      if (seq !== paymentsSeqRef.current) return;
      console.error('Failed to load payments:', err);
      // [5. javító kör, F3] korábban csak naplózott → a lista a RÉGI (akár másik edző) tartalmát
      // mutatta tovább. Most ürül, és érthető hiba jelenik meg.
      setPayments([]);
      setPaymentsError(requestErrorMessage(err, 'Nem sikerült betölteni az edző kifizetéseit.'));
      paymentsFailedBySessionRef.current = (err as any)?.response?.status === 401;
    } finally {
      if (seq === paymentsSeqRef.current) setLoadingPayments(false);
    }
  };

  const loadCoachCollected = async () => {
    const { coachId, start, end } = selectionRef.current; // [5. javító kör, F1] ref, nem closure
    if (!coachId) return;
    const seq = ++collectedSeqRef.current;
    const isCurrent = () => seq === collectedSeqRef.current;
    try {
      setLoadingCollected(true);
      collectedFailedRef.current = false;
      collectedFailedBySessionRef.current = false;
      // [5. javító kör, F2] a régi számok a kérés indulásakor eltűnnek
      setTotalCollected(null);
      setCalculatedCompensation(null);
      const stats = await trainingBalanceService.getCoachStatistics(
        new Date(start),
        new Date(end),
        false,
        coachId
      );
      if (!isCurrent()) return;
      setTotalCollected(stats.totalCollected ?? 0);
      setCalculatedCompensation(stats.calculatedCompensation ?? 0);
    } catch (err) {
      if (!isCurrent()) return;
      setTotalCollected(null);
      setCalculatedCompensation(null);
      // [8. javító kör, F-1] megjegyezzük, hogy bukott (és hogy a lejárat miatt-e), különben az
      // újra-belépés / „Újrapróbálás” után az összesítő örökre üres maradna
      collectedFailedRef.current = true;
      collectedFailedBySessionRef.current = (err as any)?.response?.status === 401;
    } finally {
      if (isCurrent()) setLoadingCollected(false);
    }
  };

  /**
   * A létrehozás csak akkor engedélyezett, ha a látott lista PONTOSAN a képernyőn választott
   * (edző, időszak) párhoz tartozik, és a dátum-mezőkben nincs még be nem töltött érték.
   */
  const canCreatePayment =
    !loadingEvents &&
    loadedPeriod !== null &&
    loadedPeriod.coachId === selectedCoachId &&
    loadedPeriod.start === startDate &&
    loadedPeriod.end === endDate &&
    startDateInput === startDate &&
    endDateInput === endDate;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /*
   * [Web 6 W2, 2026-10-05] VÉDŐHÁLÓ (d): nem-kijelölhető (Hiányzott vagy kifizetett) edzés SEMMILYEN
   * úton nem kerülhet a kijelölésbe — se egyenként (`handleSelectEvent`), se az „Összes kijelölése”
   * gombbal, se a lista újratöltésének metszetével (`loadPayableEvents`) —, és a létrehozás/összeg
   * (`handleCreatePayment`, `getSelectedEventsTotal`) ráadásul csak a kijelölhető sorokat számolja.
   * DÖNTÉS: mivel a Hiányzott sor eleve nem kerülhet be, a „Csak ahol az edző jelen volt” kapcsoló
   * váltása NEM nyúl a kijelöléshez: rejtett, de kijelölt sor így nem létezhet (a kapcsoló csak
   * Hiányzott sorokat rejt el), a jelen-sorok kijelölése pedig megmarad — a felhasználó nem veszít
   * munkát egy puszta nézet-váltással.
   */
  const payableEventsById = new Map<string, PayableEvent>(
    (payableEventsData?.events ?? []).map((e) => [e.eventId, e])
  );
  const isSelectableId = (eventId: string) => {
    const ev = payableEventsById.get(eventId);
    return ev !== undefined && isSelectableEvent(ev);
  };
  const selectableCount = (payableEventsData?.events ?? []).filter(isSelectableEvent).length;
  // [Web 6 W2, 2026-10-05] a kapcsoló CSAK a „Hiányzott” sorokat rejti el; hogy ne tűnjön el semmi
  // némán, a rejtett sorok számát a felület kimondja (`attended-only-hidden-count` / `-empty`).
  const visibleEvents = (payableEventsData?.events ?? []).filter((e) => !attendedOnly || e.coachAttended);
  const hiddenAbsentCount = (payableEventsData?.events.length ?? 0) - visibleEvents.length;

  const handleSelectEvent = (eventId: string, checked: boolean) => {
    if (checked) {
      if (!isSelectableId(eventId)) return; // [Web 6 W2] Hiányzott/kifizetett sor nem jelölhető
      setSelectedEventIds(prev => (prev.includes(eventId) ? prev : [...prev, eventId]));
    } else {
      setSelectedEventIds(prev => prev.filter(id => id !== eventId));
    }
  };

  const handleSelectAllPayable = () => {
    if (!payableEventsData) return;
    // [Web 6 W2, 2026-10-05] KORÁBBAN `.filter(e => !e.isPaid)` — a Hiányzott sorokat is kijelölte,
    // és a szerver az egész kifizetést 400-zal elutasította (`CoachPaymentController.cs:323-335`).
    const payableIds = payableEventsData.events
      .filter(isSelectableEvent)
      .map(e => e.eventId);
    setSelectedEventIds(payableIds);
  };

  const handleDeselectAll = () => {
    setSelectedEventIds([]);
  };

  /** A létrehozás hibája a lap tetején ÉS a nyitva maradó dialógusban is [5. javító kör, F4]. */
  const failCreate = (message: string) => {
    setError(message);
    setCreateError(message);
  };

  const openCreateDialog = () => {
    setCreateError(null);
    setConfirmDialogOpen(true);
  };

  /**
   * [5. javító kör, F1] A létrehozás ALATT a dialógus nem zárható (Esc, háttér, „Mégse”) — a
   * `ComposeMessageDialog` mintája szerint. Így a kérés közben nem lehet edzőt/időszakot váltani.
   */
  const closeCreateDialog = () => {
    if (creatingRef.current) return;
    setConfirmDialogOpen(false);
  };

  const handleCreatePayment = async () => {
    // [5. javító kör, kiegészítés] dupla kattintás: a második hívás a ref miatt AZONNAL kilép
    if (creatingRef.current) return;
    if (!selectedCoachId) return;
    // [4. javító kör, F-B] csak a FRISSEN betöltött (edző, időszak) párra — lásd `canCreatePayment`
    if (!canCreatePayment || !loadedPeriod) {
      failCreate('Az időszak még nincs betöltve — várd meg a lista frissülését.');
      return;
    }
    const period = loadedPeriod;

    // Validate at least one item (event or custom)
    // [Web 6 W2, 2026-10-05] védőháló: csak kijelölhető (kifizetetlen + jelen) edzés megy ki
    const eventIdsToSend = selectedEventIds.filter(isSelectableId);
    if (eventIdsToSend.length === 0 && customItems.length === 0) {
      failCreate('Legalább egy edzést vagy egyéb tételt ki kell választani');
      return;
    }

    /*
     * MIÉRT NINCS ITT MÁSODIK, BATCH-ELŐTTI ŐR a tétel-összegre [NB-3, 2026-09-14] — tudatos
     * döntés: a `customItems` listába KIZÁRÓLAG a `handleAddCustomItem` tesz elemet (tétel-szerkesztés
     * nincs, csak felvétel és törlés), tehát a felső őr ODA tartozik — ott a visszajelzés AZONNALI,
     * a mező még a szem előtt van, és túl nagy tétel EL SEM jut a listáig. Egy itteni második őr
     * a felületen ELÉRHETETLEN ág lenne, amire nem lehet őr-tesztet írni — a projekt pedig a
     * soha be nem kötött „védelmet" (lásd a törölt `utils/validation.ts`-t) több kárnak tartja,
     * mint haszonnak. ⚠️ HA VALAHA LESZ TÉTEL-SZERKESZTÉS, a felső őrt ARRA az útra is ki kell
     * terjeszteni — a kifizetés-létrehozás BATCH, tehát egy túl nagy tétel a TELJES összeállítást
     * elvinné egy technikai „HTTP 400" üzenettel.
     */

    creatingRef.current = true;
    try {
      setCreating(true);
      setError(null);
      setCreateError(null);

      // A BETÖLTÖTT pár megy ki (nem a mező pillanatnyi értéke); a UTC-éjfél szándékos, lásd `loadPayableEvents`.
      await coachPaymentService.createPayment({
        coachId: period.coachId,
        periodStart: new Date(period.start),
        periodEnd: new Date(period.end),
        eventIds: eventIdsToSend,
        customItems: customItems,
        adminNotes: adminNotes || undefined
      });

      showSuccess('A kifizetés létrehozva.');
      setConfirmDialogOpen(false);

      /*
       * [5. javító kör, F1] Az újratöltés a MINDENKORI választásra fut (a betöltők a `selectionRef`-ből
       * olvasnak), és csak akkor, ha a választás a kérés óta NEM változott — különben a váltás maga
       * már betöltötte az új (edző, időszak) párt, és egy itteni második kérés csak zavarna. Az
       * összeállítás is csak akkor ürül, ha még ugyanaz az edző: az elküldött tételek ugyanis az
       * ÖVÉI; egy közben választott másik edző összeállításához nem nyúlunk.
       * (A dialógus a létrehozás alatt nem zárható, így ez a váltás a felületről nem érhető el —
       * ez a második védvonal.)
       */
      const current = selectionRef.current;
      const sameCoach = current.coachId === period.coachId;
      const samePeriod = sameCoach && current.start === period.start && current.end === period.end;
      if (sameCoach) {
        setCustomItems([]);
        setAdminNotes('');
      }
      if (samePeriod) {
        setSelectedEventIds([]);
        await loadPayableEvents();
      }
      if (sameCoach) await loadCoachPayments();
    } catch (err: any) {
      console.error('Failed to create payment:', err);
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      // [5. javító kör, F4] a nyitva maradó dialógusban is
      failCreate(saveErrorMessage(err, 'Nem sikerült létrehozni a kifizetést'));
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  /**
   * A MEGERŐSÍTŐ ABLAK KIMONDJA, KIÉ A KIFIZETÉS [Web UX 3. csomag (C), 2026-10-04 — WEB_UX 1.7].
   *
   * MI VOLT A BAJ: a „Kifizetve" ablak csak az összeget és az órákat írta, a TÖRLÉS ablaka pedig
   * semmit („Biztosan törölni szeretnéd ezt a kifizetést?") — egy több edzős, több időszakos
   * listában az admin nem látta, MELYIK kifizetést hagyja jóvá / törli. Pénzügyi művelet.
   * A JAVÍTÁS: mindkét ablak ugyanazt a tárgy-blokkot mutatja: edző · időszak · összeg (· órák).
   *  • A KIFIZETÉS TELJES OBJEKTUMA jön a hívótól (a lista `payments.map` zárlatából — a
   *    `handleDeletePayment` korábban csak az azonosítót kapta), tehát az ablak a MEGNYOMOTT sor
   *    adatait írja, nem egy korábban kiválasztottét.
   *  • Az időszak DÁTUM-CSAK mező (`PeriodStart`/`PeriodEnd`, „Z" nélküli éjfél) → a közös
   *    `formatDateOnlyRange` (`utils/dateUtils.ts`): a nap maga, zóna-konverzió nélkül.
   *  • Az összeg UGYANAZZAL a formázóval, mint a lista sora (`formatCurrency`, lent: egész
   *    forintra kerekít) [1. validátor-kör M1, 2026-10-04]. Korábban itt a közös `formatForint`
   *    állt, ami a törtet is kiírja: 7498,5-nél a lista „7 499 Ft"-ot, az ablak „7 498,5 Ft"-ot
   *    mutatott — a felhasználó egy MÁSIK számot erősített volna meg, mint amit a listában látott.
   *    DÖNTÉS: az ablak a listát követi (nem fordítva), mert a lista-kiírás a képernyő ~15 helyén
   *    fut; a teljes képernyő `formatForint`-ra (+ egységes kerekítésre) állítása NYITOTT, külön
   *    egységesítési tétel. Őr: `CoachPaymentManagement.test.tsx` „törtösszeg" eset.
   */
  const renderPaymentSubject = (payment: CoachPayment) => (
    <Box sx={{ mt: 2 }} data-testid="payment-confirm-subject">
      <Typography><strong>Edző:</strong> {payment.coachName}</Typography>
      <Typography><strong>Időszak:</strong> {formatDateOnlyRange(payment.periodStart, payment.periodEnd, { fallback: '-' })}</Typography>
      <Typography><strong>Összeg:</strong> {formatCurrency(payment.totalAmount)}</Typography>
      <Typography><strong>Órák:</strong> {payment.totalHours}</Typography>
    </Box>
  );

  const handleMarkAsPaid = async (payment: CoachPayment) => {
    const confirmed = await confirm({
      // [2.4 helyesírás] „Kifizetés Megerősítése" → „Kifizetés megerősítése" (E2E nem hivatkozik rá).
      title: 'Kifizetés megerősítése',
      message: (
        <>
          Biztosan "Kifizetve" státuszra állítod ezt a kifizetést?
          {renderPaymentSubject(payment)}
        </>
      ),
      confirmLabel: 'Kifizetve',
    });
    if (!confirmed) return;

    try {
      await coachPaymentService.markAsPaid(payment.paymentId);
      showSuccess('A kifizetés kifizetettként jelölve.');
      await loadCoachPayments();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Nem sikerült frissíteni a státuszt'));
    }
  };

  const handleDeletePayment = async (payment: CoachPayment) => {
    const confirmed = await confirm({
      title: 'Kifizetés törlése',
      message: (
        <>
          Biztosan törölni szeretnéd ezt a kifizetést?
          {renderPaymentSubject(payment)}
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await coachPaymentService.deletePayment(payment.paymentId);
      showSuccess('A kifizetés törölve.');
      // [7. javító kör — validátor M2] egy RÉGI kifizetés törlése nem üríti a folyamatban lévő
      // összeállítás kijelölését (metszet az új listával — lásd `loadPayableEvents`)
      await loadPayableEvents({ keepSelection: true });
      await loadCoachPayments();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Nem sikerült törölni a kifizetést'));
    }
  };

  const formatDate = (dateStr: string) => {
    return format(parseISO(dateStr), 'yyyy. MM. dd.', { locale: hu });
  };

  // [Web UX 1. csomag (4.3), 2026-10-03] a `createdDate`/`paidDate`/`confirmedDate` UTC-ben, „Z” nélkül jön
  // (`CoachPaymentController.cs:669, :779`, élő API-n mérve); a `parseISO` helyi időnek vette.
  // A dátum-csak `formatDate` (fent) helyes: a `parseISO('yyyy-MM-dd')` helyi éjfelet ad.
  const formatDateTime = (dateStr: string) => formatServerDateTime(dateStr, { fallback: '-' });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(amount);
  };

  const getSelectedEventsTotal = () => {
    const selectedEvents = payableEventsData
      ? payableEventsData.events.filter(e => isSelectableEvent(e) && selectedEventIds.includes(e.eventId)) // [Web 6 W2] védőháló
      : [];

    const eventTotals = selectedEvents.reduce(
      (acc, e) => ({ hours: acc.hours + e.hours, amount: acc.amount + e.amount }),
      { hours: 0, amount: 0 }
    );

    const collectedDeduction = selectedEvents.reduce((sum, e) => sum + (e.collectedAmount || 0), 0);

    const customItemTotals = customItems.reduce(
      (acc, item) => ({
        hours: acc.hours + (item.hours || 0),
        amount: acc.amount + item.amount
      }),
      { hours: 0, amount: 0 }
    );

    const grossAmount = eventTotals.amount + customItemTotals.amount;
    return {
      hours: eventTotals.hours + customItemTotals.hours,
      amount: grossAmount - collectedDeduction,
      grossAmount,
      collectedDeduction
    };
  };

  const handleAddCustomItem = () => {
    /**
     * ⚠️ `=== undefined`, NEM `!customItemForm.amount` [NB-8, 2026-09-14]: a `0` FALSY, tehát a
     * korábbi feltétel a NULLA forintos tételt „nincs megadva"-ként kezelte, és nem engedte
     * felvenni (a gomb is tiltva maradt). A 0 Ft ÉRDEMI ÉRTÉK („ingyen" — pl. dokumentált, de
     * nem elszámolt utazás); a „nincs megadva" az ÜRES mező, ahogy a `utils/money.ts` kimondja.
     */
    if (!customItemForm.description || customItemForm.amount === undefined) {
      setError('Kérlek töltsd ki a kötelező mezőket (leírás, összeg)');
      return;
    }

    /**
     * FELSŐ ŐR [NB-3, 2026-09-14] — ugyanaz a minta, mint a jelenléti íven
     * (`MAX_ATTENDANCE_PAYMENT`): a kifizetés-létrehozás BATCH, tehát EGY túl nagy tétel a TELJES
     * összeállított kifizetést buktatná egy technikai „HTTP 400" üzenettel (a szerver magyar
     * indoklása a válasz `errors` mezőjében marad, amit a felület nem mutat meg). Ezért már a
     * FELVÉTELKOR megállunk, a TÉTEL MEGNEVEZÉSÉVEL, hogy látszódjon, melyikről van szó.
     */
    if (customItemForm.amount > MAX_CUSTOM_ITEM_AMOUNT) {
      setError(
        `Túl nagy összeg: ${customItemForm.description} — ` +
        `${MAX_CUSTOM_ITEM_AMOUNT.toLocaleString('hu-HU')} Ft-nál több nem adható meg egy tételre.`
      );
      return;
    }

    const newItem: CustomPaymentItem = {
      category: customItemForm.category || 'Other',
      description: customItemForm.description,
      date: customItemForm.date || new Date(),
      hours: customItemForm.hours,
      amount: customItemForm.amount
    };

    setCustomItems([...customItems, newItem]);
    setCustomItemForm({
      category: 'Travel' as PaymentItemCategory,
      description: '',
      date: new Date(),
      hours: undefined,
      // ÜRES mező a felvétel után — a korábbi `0` egy „0"-t mutató mezőt hagyott maga után [NB-8].
      amount: undefined
    });
    setError(null);
  };

  const handleRemoveCustomItem = (index: number) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const totals = getSelectedEventsTotal();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Edző Kifizetések Nyilvántartása
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Kifizetés Létrehozása" />
        <Tab label="Kifizetések Listája" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {/* Coach and Date Selection */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edző és Időszak Kiválasztása
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Edző</InputLabel>
                <Select
                  value={selectedCoachId}
                  label="Edző"
                  onChange={async (e) => {
                    const next = e.target.value;
                    if (next === selectedCoachId) return;
                    // [3. javító kör, F-2] a teljes összeállítás ürül — lásd `confirmCoachChange`
                    if (await confirmCoachChange()) {
                      resetComposition();
                      // [4. javító kör, F-B] a régi edző listája azonnal ürül, késői válasza érvénytelen
                      // [5. javító kör, F2] … az összesítője is
                      invalidatePayableEvents();
                      // [5. javító kör, F3] … és a kifizetés-előzménye is
                      invalidateCoachPayments();
                      setSelectedCoachId(next);
                    }
                  }}
                  disabled={loadingCoaches}
                >
                  {coaches.map(coach => (
                    <MenuItem key={coach.userId} value={coach.userId}>
                      {coach.name} {coach.hourlyRate ? `(${formatCurrency(coach.hourlyRate)}/óra)` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Kezdő dátum"
                type="date"
                value={startDateInput}
                onChange={(e) => handleDateInput('start', e.target.value)}
                onBlur={() => { void commitDateInput('start'); }}
                onKeyDown={(e) => handleDateKeyDown('start', e)}
                helperText={datePending('start') ? 'Enter vagy a mező elhagyása: betöltés' : undefined}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Záró dátum"
                type="date"
                value={endDateInput}
                onChange={(e) => handleDateInput('end', e.target.value)}
                onBlur={() => { void commitDateInput('end'); }}
                onKeyDown={(e) => handleDateKeyDown('end', e)}
                helperText={datePending('end') ? 'Enter vagy a mező elhagyása: betöltés' : undefined}
                InputLabelProps={{ shrink: true }}
              />

              <Button
                variant="outlined"
                onClick={async () => {
                  if (await confirmDropSelection('refresh')) loadPayableEvents();
                }}
                disabled={!selectedCoachId || loadingEvents}
              >
                Frissítés
              </Button>
            </Box>
          </Paper>

          {/* Payable Events */}
          {selectedCoachId && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Kifizetendő Edzések
                </Typography>
                {payableEventsData && (
                  <Box>
                    <Button
                      size="small"
                      onClick={handleSelectAllPayable}
                      sx={{ mr: 1 }}
                      disabled={selectableCount === 0}
                      data-testid="select-all-payable"
                    >
                      Összes kijelölése
                    </Button>
                    <Button size="small" onClick={handleDeselectAll}>
                      Kijelölés törlése
                    </Button>
                  </Box>
                )}
              </Box>

              {loadingEvents ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : payableEventsData ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>{payableEventsData.coachName}</strong> -
                    Óradíj: {payableEventsData.hourlyRate ? formatCurrency(payableEventsData.hourlyRate) : 'Nincs beállítva'} |
                    Összes edzés: {payableEventsData.totalEvents} |
                    Kifizetett: {payableEventsData.paidEvents} |
                    Kifizetendő: {payableEventsData.payableEvents}
                    {totalCollected !== null && (
                      <> | <strong>Beszedett: {formatCurrency(totalCollected)}</strong></>
                    )}
                    {/* [5. javító kör, F2] amíg az új edző/időszak összesítője úton van, nem a régi számok látszanak */}
                    {totalCollected === null && loadingCollected && (
                      <> | <span data-testid="collected-loading">Beszedett / Fizetendő: betöltés…</span></>
                    )}
                    {calculatedCompensation !== null && totalCollected !== null && (
                      <>
                        {' '}| Keresett: {formatCurrency(calculatedCompensation)}
                        {' '}| <strong style={{ color: (calculatedCompensation - totalCollected) > 0 ? '#d32f2f' : '#2e7d32' }}>
                          Fizetendő: {formatCurrency(calculatedCompensation - totalCollected)}
                        </strong>
                      </>
                    )}
                  </Alert>

                  {payableEventsData.events.length === 0 ? (
                    <Alert severity="warning">
                      Nincs edzés ebben az időszakban.
                    </Alert>
                  ) : (
                    <>
                    {/* [Web 6 W2, 2026-10-05] klubvezetés: „Edzők kifizetésénél lehetne szűrni a megjelent
                        eseményekre, mert ebből van a legtöbb.” Alapból BE; kikapcsolva a Hiányzott sorok is
                        látszanak, de tiltott jelölővel. A kijelöléshez a váltás nem nyúl (lásd `isSelectableId`). */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={attendedOnly}
                            onChange={(e) => setAttendedOnly(e.target.checked)}
                            // MUI 7: a Switch a saját `slotProps.input`-jával ({ role: 'switch' }) FELÜLÍRJA az
                            // `inputProps`-ot, ezért a teszt-azonosító itt, a szerepkörrel EGYÜTT megy.
                            slotProps={{ input: { role: 'switch', 'data-testid': 'attended-only-switch' } as any }}
                          />
                        }
                        label="Csak ahol az edző jelen volt"
                      />
                      {hiddenAbsentCount > 0 && visibleEvents.length > 0 && (
                        <Typography variant="body2" color="text.secondary" data-testid="attended-only-hidden-count">
                          {hiddenAbsentCount} hiányzott alkalom elrejtve
                        </Typography>
                      )}
                    </Box>
                    {visibleEvents.length === 0 ? (
                      <Alert severity="info" data-testid="attended-only-empty">
                        Nincs kifizethető, jelen volt edzés ebben az időszakban. A hiányzott alkalmak
                        ({hiddenAbsentCount}) a kapcsoló kikapcsolásával látszanak.
                      </Alert>
                    ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Dátum</TableCell>
                            <TableCell>Esemény</TableCell>
                            <TableCell>Idő</TableCell>
                            <TableCell align="right">Órák</TableCell>
                            <TableCell align="right">Összeg</TableCell>
                            <TableCell align="right">Beszedett</TableCell>
                            <TableCell>Jelenlét</TableCell>
                            <TableCell>Státusz</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleEvents.map((event) => (
                            <TableRow
                              key={event.eventId}
                              sx={{
                                opacity: event.isPaid ? 0.6 : 1,
                                bgcolor: selectedEventIds.includes(event.eventId) ? 'action.selected' : 'inherit'
                              }}
                            >
                              <TableCell padding="checkbox">
                                {!event.isPaid && !event.coachAttended ? (
                                  // [Web 6 W2] Hiányzott sor: a jelölő TILTOTT; a tiltott elemre a MUI
                                  // Tooltip nem reagál, ezért `<span>` burok viszi az egér-eseményt.
                                  <Tooltip title={ABSENT_NOT_PAYABLE_TOOLTIP}>
                                    <span data-testid={`absent-not-payable-${event.eventId}`}>
                                      <Checkbox
                                        checked={false}
                                        disabled
                                        inputProps={{ 'aria-label': ABSENT_NOT_PAYABLE_TOOLTIP }}
                                      />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Checkbox
                                    checked={selectedEventIds.includes(event.eventId)}
                                    onChange={(e) => handleSelectEvent(event.eventId, e.target.checked)}
                                    disabled={!isSelectableEvent(event)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>{formatDate(event.eventDate)}</TableCell>
                              <TableCell>{event.eventName}</TableCell>
                              <TableCell>{event.startTime} - {event.endTime}</TableCell>
                              <TableCell align="right">
                                {event.hours}
                                {event.eventHours != null && event.hours < event.eventHours && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={`részleges · ${event.eventHours} órából${event.presentFrom && event.presentUntil ? ` (${event.presentFrom.substring(0, 5)}–${event.presentUntil.substring(0, 5)})` : ''}`}
                                    sx={{ ml: 1 }}
                                    data-testid={`partial-hours-${event.eventId}`}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(event.amount)}</TableCell>
                              <TableCell align="right">
                                {event.collectedAmount > 0 ? (
                                  <Chip size="small" label={formatCurrency(event.collectedAmount)} color="warning" />
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={event.coachAttended ? 'Jelen' : 'Hiányzott'}
                                  color={event.coachAttended ? 'success' : 'error'}
                                />
                              </TableCell>
                              <TableCell>
                                {event.isPaid ? (
                                  <Chip
                                    size="small"
                                    label={PaymentStatusDisplayNames[event.paymentStatus!]}
                                    color={PaymentStatusColors[event.paymentStatus!] as any}
                                  />
                                ) : (
                                  <Chip size="small" label="Kifizetetlen" variant="outlined" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    )}
                    </>
                  )}

                  {/* Custom Payment Items Section */}
                  <Paper sx={{ p: 2, mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Egyéb Tételek Hozzáadása
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Itt adhatsz hozzá egyéb kifizetési tételeket (útiköltség, bónusz, stb.)
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Kategória</InputLabel>
                        <Select
                          value={customItemForm.category || 'Travel'}
                          onChange={(e) => setCustomItemForm({ ...customItemForm, category: e.target.value as PaymentItemCategory })}
                          label="Kategória"
                        >
                          <MenuItem value="Travel">{PaymentItemCategoryDisplayNames.Travel}</MenuItem>
                          <MenuItem value="Bonus">{PaymentItemCategoryDisplayNames.Bonus}</MenuItem>
                          <MenuItem value="Other">{PaymentItemCategoryDisplayNames.Other}</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        size="small"
                        fullWidth
                        label="Leírás"
                        value={customItemForm.description || ''}
                        onChange={(e) => setCustomItemForm({ ...customItemForm, description: e.target.value })}
                        placeholder="Pl. Útiköltség Budapestre"
                        required
                      />

                      <TextField
                        size="small"
                        fullWidth
                        type="date"
                        label="Dátum"
                        value={customItemForm.date ? format(customItemForm.date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')}
                        onChange={(e) => setCustomItemForm({ ...customItemForm, date: new Date(e.target.value) })}
                        InputLabelProps={{ shrink: true }}
                      />

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          size="small"
                          fullWidth
                          // AZ ÓRA NEM PÉNZ: itt a tizedes SZÁNDÉKOS és indokolt (`step: 0.5` —
                          // a fél óra valódi eset), ezért ez a mező MARAD `type="number"` +
                          // `parseFloat`. A pénz-mezők közös, EGÉSZ forintos szabálya (BL-2,
                          // `utils/money.ts`) ide NEM vonatkozik — a szomszédos „Összeg (Ft)"
                          // mezőnél viszont igen.
                          type="number"
                          label="Órák (opcionális)"
                          value={customItemForm.hours ?? ''}
                          onChange={(e) => setCustomItemForm({ ...customItemForm, hours: e.target.value ? parseFloat(e.target.value) : undefined })}
                          placeholder="0"
                          inputProps={{ min: 0, step: 0.5 }}
                        />

                        <TextField
                          size="small"
                          fullWidth
                          // `type="text"` + `inputMode="numeric"`, NEM `type="number"`
                          // [BL-2, 2026-09-14]: a `parseFloat('2.800')` 2,8-at ad, tehát a
                          // magyarul gépelt kétezer-nyolcszázból 2,8 Ft-os tétel lett volna, NÉMÁN.
                          // A bevitel a KÖZÖS forint-szabályon él (`utils/money.ts`).
                          type="text"
                          label="Összeg (Ft)"
                          value={forintFieldValue(customItemForm.amount)}
                          onChange={(e) => {
                            const value = e.target.value;
                            // A MOSTANI érték is megy [J-3]: egy régi, tizedes tétel javítható.
                            if (!isForintInputAccepted(value, forintFieldValue(customItemForm.amount))) return;
                            setCustomItemForm({ ...customItemForm, amount: parseForintInput(value) });
                          }}
                          required
                          inputProps={{ inputMode: 'numeric', 'data-testid': 'custom-item-amount-input' }}
                        />
                      </Box>

                      <Button
                        variant="outlined"
                        onClick={handleAddCustomItem}
                        // `=== undefined`, NEM `!...amount` [NB-8]: a 0 Ft érdemi érték, tehát
                        // a 0-val kitöltött tétel is felvehető; csak az ÜRES mező tiltja a gombot.
                        disabled={!customItemForm.description || customItemForm.amount === undefined}
                      >
                        Tétel Hozzáadása
                      </Button>
                    </Box>

                    {/* List of added custom items */}
                    {customItems.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Hozzáadott tételek ({customItems.length})
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Kategória</TableCell>
                                <TableCell>Leírás</TableCell>
                                <TableCell>Dátum</TableCell>
                                <TableCell align="right">Órák</TableCell>
                                <TableCell align="right">Összeg</TableCell>
                                <TableCell align="center">Művelet</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customItems.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={PaymentItemCategoryDisplayNames[item.category]}
                                      color="default"
                                    />
                                  </TableCell>
                                  <TableCell>{item.description}</TableCell>
                                  <TableCell>{format(item.date, 'yyyy. MM. dd.')}</TableCell>
                                  <TableCell align="right">{item.hours || '-'}</TableCell>
                                  <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveCustomItem(index)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Paper>

                  {/* Selection Summary */}
                  {(selectedEventIds.length > 0 || customItems.length > 0) && (
                    /*
                      [Web 7 / T2, 2026-10-06] WEB_UX_2KOR (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 1.5: a `primary.light` háttéren a `primary.contrastText`
                      világosban 3,31:1, sötétben (a Paper fehér rétegével) 2,77:1 volt. A `primary.main` +
                      `primary.contrastText` pár mindkét témában ≥ 4,5:1 — a recept (`paymentSummaryColors`) és
                      a kontraszt-őr ugyanabból a függvényből számol.
                    */
                    <Paper
                      elevation={PAYMENT_SUMMARY_ELEVATION}
                      data-testid="payment-selection-summary"
                      sx={{
                        p: 2,
                        mt: 2,
                        bgcolor: (t: Theme) => paymentSummaryColors(t).bg,
                        color: (t: Theme) => paymentSummaryColors(t).text,
                        // A sötét téma a `body1`-nek SAJÁT színt ad (#CFD8DC, `darkTheme.ts` typography) —
                        // az „Összesen" sor így NEM örökölte a contrastText-et: MÉRVE 4,10:1 (E2E
                        // `flow-readable-highlights`). Az összesítő minden szövege EXPLICIT a recept színét kapja.
                        '& .MuiTypography-root': { color: (t: Theme) => paymentSummaryColors(t).text },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                          <Typography variant="h6">
                            Kiválasztva: {selectedEventIds.length} edzés
                            {customItems.length > 0 && ` + ${customItems.length} egyéb tétel`}
                          </Typography>
                          <Typography>
                            Összesen: {totals.hours.toFixed(2)} óra = {formatCurrency(totals.grossAmount)}
                            {totals.collectedDeduction > 0 && (
                              <> − Beszedett: {formatCurrency(totals.collectedDeduction)}</>
                            )}
                          </Typography>
                          {totals.collectedDeduction > 0 && (
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              Nettó kifizetés: {formatCurrency(totals.amount)}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<PaymentIcon />}
                          onClick={openCreateDialog}
                          disabled={!canCreatePayment}
                        >
                          Kifizetés Létrehozása
                        </Button>
                      </Box>
                    </Paper>
                  )}
                </>
              ) : payableError ? (
                // [7. javító kör — validátor M1] hibánál NEM a „Válassz edzőt…" súgó (mindkettő ki van
                // választva), hanem a hiba és az újrapróbálás.
                // [8. javító kör — 6. validátor M-b] PONTOSÍTVA: a kijelölés és a felvett egyéb tételek
                // az ÁLLAPOTBAN megmaradnak, de hiba alatt NEM látszanak (a tétel-blokk a betöltött lista
                // ágában van); sikeres újratöltés után újra megjelennek. Szándékosan nem hozzuk ki ide:
                // a kijelölés-összesítő olyan edzésekre hivatkozna, amelyek listája nem látszik.
                // [8. javító kör, F-1] az „Újrapróbálás" a többi HIBÁS betöltést is újratölti
                <Alert
                  severity="error"
                  data-testid="payable-load-error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={retryFailedLoads}
                      data-testid="payable-retry-button"
                    >
                      Újrapróbálás
                    </Button>
                  }
                >
                  {payableError}
                </Alert>
              ) : (
                <Alert severity="info">
                  Válassz edzőt és időszakot a kifizetendő edzések megjelenítéséhez.
                </Alert>
              )}
            </Paper>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {!selectedCoachId ? (
            <Alert severity="info">
              Válassz edzőt a kifizetések megtekintéséhez.
            </Alert>
          ) : loadingPayments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : paymentsError ? (
            // [5. javító kör, F3] hibánál üres lista + érthető üzenet (nem a régi edző kifizetései)
            // [8. javító kör, F-1] saját „Újrapróbálás" (lejáratnál az újra-belépés magától újratölt)
            <Alert
              severity="error"
              data-testid="payments-load-error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => { void loadCoachPayments(); }}
                  data-testid="payments-retry-button"
                >
                  Újrapróbálás
                </Button>
              }
            >
              {paymentsError}
            </Alert>
          ) : payments.length === 0 ? (
            <Alert severity="info">
              Még nincsenek kifizetések rögzítve ennél az edzőnél.
            </Alert>
          ) : (
            <Box>
              {payments.map((payment) => (
                <Accordion key={payment.paymentId} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {payment.coachName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {payment.totalHours} óra = {formatCurrency(payment.totalAmount)}
                      </Typography>
                      <Chip
                        size="small"
                        label={PaymentStatusDisplayNames[payment.status]}
                        color={PaymentStatusColors[payment.status] as any}
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      {payment.status === 'PendingAdminReview' && (
                        <>
                          <Tooltip title="Kifizetve jelölés">
                            <IconButton
                              color="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsPaid(payment);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Törlés">
                            <IconButton
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                // a TELJES objektum — az ablak ennek a sornak az adatait mondja ki [1.7]
                                handleDeletePayment(payment);
                              }}
                              aria-label="Kifizetés törlése"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Létrehozva:</Typography>
                        <Typography>{formatDateTime(payment.createdDate)} ({payment.createdByName})</Typography>
                      </Box>
                      {payment.paidDate && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Kifizetve:</Typography>
                          <Typography>{formatDateTime(payment.paidDate)} ({payment.paidByName})</Typography>
                        </Box>
                      )}
                      {payment.confirmedDate && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Visszaigazolva:</Typography>
                          <Typography>{formatDateTime(payment.confirmedDate)}</Typography>
                        </Box>
                      )}
                    </Box>

                    {payment.adminNotes && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Admin megjegyzés:</strong> {payment.adminNotes}
                      </Alert>
                    )}

                    {payment.coachNotes && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Edző megjegyzés:</strong> {payment.coachNotes}
                      </Alert>
                    )}

                    <Typography variant="subtitle2" gutterBottom>
                      Kifizetési tételek ({payment.paymentEvents.length} db):
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kategória</TableCell>
                            <TableCell>Dátum</TableCell>
                            <TableCell>Esemény/Leírás</TableCell>
                            <TableCell>Idő</TableCell>
                            <TableCell align="right">Órák</TableCell>
                            <TableCell align="right">Összeg</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payment.paymentEvents.map((event) => (
                            <TableRow key={event.paymentEventId}>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={PaymentItemCategoryDisplayNames[event.category as PaymentItemCategory] || event.category}
                                  color={event.category === 'Training' ? 'primary' : event.category === 'Deduction' ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell>{formatDate(event.eventDate)}</TableCell>
                              <TableCell>
                                {event.isManualEntry && event.description ? event.description : event.eventName}
                              </TableCell>
                              <TableCell>
                                {event.startTime && event.endTime
                                  ? `${event.startTime.substring(0, 5)} - ${event.endTime.substring(0, 5)}`
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {event.hours || '-'}
                                {event.eventHours != null && event.hours > 0 && event.hours < event.eventHours && (
                                  <Chip size="small" color="warning" variant="outlined" label={`részleges · ${event.eventHours} órából`} sx={{ ml: 1 }} />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ color: event.amount < 0 ? 'error.main' : 'inherit', fontWeight: event.amount < 0 ? 'bold' : 'normal' }}>
                                {formatCurrency(event.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Create Payment Confirmation Dialog */}
      {/* [5. javító kör, F1] a létrehozás alatt Esc/háttér/„Mégse” nem zár (`closeCreateDialog`) */}
      <Dialog open={confirmDialogOpen} onClose={creating ? undefined : closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Kifizetés Létrehozása</DialogTitle>
        <DialogContent>
          {/* [5. javító kör, F4] korábban a hiba csak a lap tetején, a dialógus MÖGÖTT látszott */}
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="create-payment-error">
              {createError}
            </Alert>
          )}
          <Typography gutterBottom>
            <strong>Edző:</strong> {payableEventsData?.coachName}
          </Typography>
          <Typography gutterBottom>
            <strong>Időszak:</strong> {loadedPeriod ? `${loadedPeriod.start} - ${loadedPeriod.end}` : '—'}
          </Typography>
          <Typography gutterBottom>
            <strong>Kiválasztott edzések:</strong> {selectedEventIds.length} db
          </Typography>
          {customItems.length > 0 && (
            <Typography gutterBottom>
              <strong>Egyéb tételek:</strong> {customItems.length} db
            </Typography>
          )}
          <Typography gutterBottom>
            <strong>Bruttó:</strong> {totals.hours.toFixed(2)} óra = {formatCurrency(totals.grossAmount)}
          </Typography>
          {totals.collectedDeduction > 0 && (
            <Typography gutterBottom color="warning.main">
              <strong>Beszedett levonás:</strong> -{formatCurrency(totals.collectedDeduction)}
            </Typography>
          )}
          <Typography gutterBottom variant="h6">
            <strong>Kifizetendő:</strong> {formatCurrency(totals.amount)}
          </Typography>

          <TextField
            label="Admin megjegyzés (opcionális)"
            fullWidth
            multiline
            rows={3}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creating}>Mégse</Button>
          <Button
            onClick={handleCreatePayment}
            variant="contained"
            color="primary"
            disabled={creating || !canCreatePayment}
          >
            {creating ? <CircularProgress size={24} /> : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CoachPaymentManagement;
